// Runtime validation for AI-generated courses. The Text-to-Course
// panel feeds untrusted JSON (from the model) through `parseCourse`
// before it ever reaches the registry or the runner, so a malformed
// response degrades to a clear error instead of crashing the modal or
// the course player. Mirrors the `Course` / `CourseStep` shape in
// `types.ts`; anything that doesn't fit is rejected.

import type { Course, CourseMode, CourseStep, ExperimentSpec } from "./types";
import { MODES } from "./types";

const ANIMATIONS = ["wave", "happy", "talk", "idle", "walk"];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseSpec(v: unknown): ExperimentSpec | null {
  if (!isRecord(v)) return null;
  const name = typeof v.name === "string" ? v.name : "course_step";
  const grid = isRecord(v.grid) ? v.grid : {};
  const width = typeof grid.width === "number" ? grid.width : 256;
  const height = typeof grid.height === "number" ? grid.height : 256;
  if (!Array.isArray(v.actions)) return null;
  const actions: NonNullable<ExperimentSpec["actions"]> = [];
  for (const a of v.actions) {
    if (!isRecord(a)) continue;
    if (typeof a.kind !== "string") continue;
    const tick = typeof a.tick === "number" ? a.tick : 0;
    actions.push({ tick, kind: a.kind, params: a.params ?? {} });
  }
  if (actions.length === 0) return null;
  return {
    name,
    grid: { width, height },
    seed: typeof v.seed === "number" ? v.seed : 0,
    actions,
  };
}

function parseStep(v: unknown): CourseStep | null {
  if (!isRecord(v) || typeof v.kind !== "string") return null;
  switch (v.kind) {
    case "narrate": {
      if (typeof v.says !== "string" || v.says.length === 0) return null;
      const animation =
        typeof v.animation === "string" && ANIMATIONS.includes(v.animation)
          ? (v.animation as CourseStep extends { animation?: infer A } ? A : never)
          : undefined;
      return animation
        ? { kind: "narrate", says: v.says, animation }
        : { kind: "narrate", says: v.says };
    }
    case "wait": {
      const ticks = typeof v.ticks === "number" ? Math.round(v.ticks) : 0;
      if (ticks <= 0) return null;
      return { kind: "wait", ticks: Math.min(ticks, 600) };
    }
    case "experiment": {
      const spec = parseSpec(v.spec);
      return spec ? { kind: "experiment", spec } : null;
    }
    default:
      return null;
  }
}

/** Parse an AI-returned course (object or JSON string) into a safe
 * `Course`, or `null` if it's malformed. `forceMode` pins the mode to
 * the difficulty the user picked, regardless of what the model wrote.
 * `preserveId` keeps the input's existing id verbatim (used when
 * re-hydrating a stored course so it dedups stably) instead of minting
 * a fresh `ai_…_timestamp` one. */
export function parseCourse(
  input: unknown,
  forceMode?: CourseMode,
  preserveId = false,
): Course | null {
  let obj: unknown = input;
  if (typeof input === "string") {
    try {
      obj = JSON.parse(input);
    } catch {
      return null;
    }
  }
  if (!isRecord(obj)) return null;

  const rawMode = typeof obj.mode === "string" ? obj.mode : "explorer";
  const mode: CourseMode =
    forceMode ?? (MODES.includes(rawMode as CourseMode) ? (rawMode as CourseMode) : "explorer");

  const title = typeof obj.title === "string" && obj.title.trim() ? obj.title.trim() : "Untitled Course";
  const summary =
    typeof obj.summary === "string" && obj.summary.trim()
      ? obj.summary.trim()
      : "An AI-generated course.";

  if (!Array.isArray(obj.steps)) return null;
  const steps: CourseStep[] = [];
  for (const s of obj.steps) {
    const step = parseStep(s);
    if (step) steps.push(step);
  }
  // Need at least one scene + one narration to be worth playing.
  const hasScene = steps.some((s) => s.kind === "experiment");
  const hasNarration = steps.some((s) => s.kind === "narrate");
  if (!hasScene || !hasNarration) return null;

  // Stable, collision-resistant id (prefix marks it as user-generated).
  // When re-hydrating a stored course, keep its id so it dedups across
  // reloads; otherwise mint a fresh one at generation time.
  let id: string;
  if (preserveId && typeof obj.id === "string" && obj.id.length > 0) {
    id = obj.id;
  } else {
    const baseId =
      typeof obj.id === "string" && /^[a-z0-9_]+$/.test(obj.id) ? obj.id : "ai_course";
    id = `ai_${baseId}_${Date.now().toString(36)}`;
  }

  return { id, mode, title, summary, steps };
}
